import { Icon } from '@iconify/react';
import { useInternationalization } from '@progress/kendo-react-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { ShoppingCartContext } from './ShoppingCartContext';
import ShoppingCartSupplierCard from './ShoppingCartSupplierCard';

/** Tile width (230) + gap (12) — one card step per arrow click */
const SUPPLIER_TILE_SCROLL_STEP = 242;

const ShoppingCartSupplierCards = () => {
  const { t } = useTranslation();
  const intl = useInternationalization();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const supplierCardSummaries = useContextSelector(
    ShoppingCartContext,
    (context) => context.supplierCardSummaries
  );
  const toggleSupplierSelection = useContextSelector(
    ShoppingCartContext,
    (context) => context.toggleSupplierSelection
  );
  const openSupplierInfo = useContextSelector(
    ShoppingCartContext,
    (context) => context.openSupplierInfo
  );

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScrollLeft = scrollWidth - clientWidth;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(maxScrollLeft > 1 && scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    updateScrollState();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });
    resizeObserver.observe(el);
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild);
    }

    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [supplierCardSummaries, updateScrollState]);

  const handleSupplierInfoClick = useCallback(
    (supplierClientId: string) => {
      const supplier = supplierCardSummaries.find(
        (item) => String(item.supplier.supplierClientId) === supplierClientId
      )?.supplier;
      if (supplier) {
        openSupplierInfo(supplier);
      }
    },
    [openSupplierInfo, supplierCardSummaries]
  );

  const scrollTiles = useCallback((direction: -1 | 1) => {
    scrollRef.current?.scrollBy({
      left: direction * SUPPLIER_TILE_SCROLL_STEP,
      behavior: 'smooth',
    });
  }, []);

  if (supplierCardSummaries.length === 0) {
    return null;
  }

  const showScrollArrows = canScrollLeft || canScrollRight;

  return (
    <div className="mt-4 flex w-full min-w-0 max-w-full items-center gap-2 px-4">
      {showScrollArrows ? (
        <button
          type="button"
          aria-label={t('Scroll left')}
          disabled={!canScrollLeft}
          onClick={() => scrollTiles(-1)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#BABABA] bg-white text-[#3F6578] shadow-sm enabled:hover:bg-[#F5F5F5] disabled:cursor-default disabled:opacity-40"
          data-testid="shopping-cart-supplier-cards-scroll-left"
        >
          <Icon icon="lucide:chevron-left" height={20} width={20} />
        </button>
      ) : null}

      <div
        ref={scrollRef}
        className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        data-testid="shopping-cart-supplier-cards"
      >
        <div className="flex w-max gap-3">
          {supplierCardSummaries.map((summary) => {
            const supplierId = String(summary.supplier.supplierClientId);
            const { total } = summary.supplier;
            const formattedTotalAmount = intl.format('{0:c2}', Math.abs(total));
            const displayTotalAmount = total < 0 ? `(${formattedTotalAmount})` : formattedTotalAmount;

            return (
              <ShoppingCartSupplierCard
                key={supplierId}
                id={supplierId}
                name={summary.supplier.supplierCompanyName}
                totalFormatted={displayTotalAmount}
                itemCount={summary.supplier.quantity}
                selected={summary.selected}
                onSelectionChange={toggleSupplierSelection}
                onSupplierInfoClick={handleSupplierInfoClick}
              />
            );
          })}
        </div>
      </div>

      {showScrollArrows ? (
        <button
          type="button"
          aria-label={t('Scroll right')}
          disabled={!canScrollRight}
          onClick={() => scrollTiles(1)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#BABABA] bg-white text-[#3F6578] shadow-sm enabled:hover:bg-[#F5F5F5] disabled:cursor-default disabled:opacity-40"
          data-testid="shopping-cart-supplier-cards-scroll-right"
        >
          <Icon icon="lucide:chevron-right" height={20} width={20} />
        </button>
      ) : null}
    </div>
  );
};

export default ShoppingCartSupplierCards;
